import { cn } from "@workspace/ui/lib/utils";
import { observer } from "mobx-react-lite";
import { Children, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { company } from "~/company/Company";
import Icon from "~/components/Icon/Icon";
import type { IconTypes } from "~/components/Icon/IconTypes";
import { mainStore } from "~/stores";

import {
  CAPTION_SKELETON_DEFAULT_WIDTH,
  TITLE_SKELETON_DEFAULT_WIDTH,
  VariantMap,
} from "./constants";
import type { MobileHeaderCardProps, SkeletonSize } from "./interfaces";
import styles from "./MobileHeaderCard.module.scss";
import Overlay from "./Overlay";
import { OrderCardSkeleton } from "./Skeleton";

const MobileHeaderCard = ({
  observerKey,
  icon,
  animateIcon,
  isLoading,
  caption,
  title,
  backgroundVariant,
  children,
  onClick,
  onTransitionEnd,
  className,
  dropdownClassName,
  overlayClassName,
  overlayComponent: OverlayComponent = Overlay,
  showChevron = true,
}: MobileHeaderCardProps) => {
  const { pathname } = useLocation();

  const rootRef = useRef<Nullable<HTMLDivElement>>(null);
  const dropdownRef = useRef<Nullable<HTMLDivElement>>(null);

  const isOpen = mainStore.isHeaderDropdownOpen(observerKey);
  const [shouldRenderOverlay, setShouldRenderOverlay] = useState(isOpen);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const skeletonSize = useRef<SkeletonSize>({
    caption: CAPTION_SKELETON_DEFAULT_WIDTH,
    title: TITLE_SKELETON_DEFAULT_WIDTH,
  });

  const setIsOpen = (val: boolean) =>
    mainStore.setHeaderDropdownOpen(observerKey, val);

  const variant = backgroundVariant
    ? VariantMap.get(backgroundVariant)
    : undefined;

  const isHomePage = pathname === "/";

  const isEmptyChildren = !children || Children.count(children) === 0;

  const showContent = Boolean(caption || title);
  const showArrowSection = !isEmptyChildren && showChevron;
  const arrowContent = (
    <div
      className={cn(
        styles.selectIcon,
        isOpen && styles.opened,
        "transition-all! duration-300!",
        company.match({
          mcosmetic: isHomePage
            ? "text-navigation-text-in-button! group-data-[scrolled=true]:text-navigation-text-primary!"
            : "text-navigation-text-primary",
          default: "text-navigation-text-secondary",
        }),
      )}
    >
      <Icon type="chevron-up" size={24} />
    </div>
  );

  const handleOverlayTransitionEnd = (
    e: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (!isOverlayVisible && e.propertyName === "opacity") {
      setShouldRenderOverlay(false);
    }
    onTransitionEnd?.(e);
  };

  const renderDropdown = () => {
    if (
      typeof document === "undefined" ||
      isEmptyChildren ||
      !shouldRenderOverlay
    ) {
      return null;
    }

    return createPortal(
      <OverlayComponent
        className={overlayClassName}
        onTransitionEnd={handleOverlayTransitionEnd}
        isOpen={isOverlayVisible}
      >
        <div
          className={cn([styles.dropdown, dropdownClassName])}
          ref={dropdownRef}
        >
          {children}
        </div>
      </OverlayComponent>,
      document.body,
    );
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRenderOverlay(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsOverlayVisible(true);
        });
      });
    } else {
      setIsOverlayVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const listener = (e: Event) => {
      const dropdownElement = dropdownRef.current;
      if (!isOpen || !dropdownElement || isEmptyChildren) {
        return;
      }

      const targetElement = e.target as HTMLElement;
      if (
        dropdownElement.contains(targetElement) ||
        rootRef.current?.contains(targetElement)
      ) {
        return;
      }

      setIsOpen(false);
    };

    window.addEventListener("click", listener);
    return () => {
      window.removeEventListener("click", listener);
    };
  }, [isOpen, isEmptyChildren]);

  return (
    <>
      <div
        className={cn([
          styles.root,
          (!icon || typeof icon === "string") && styles.styledButton,
          icon && [styles.withIcon],
          showContent && styles.withContent,
          !isLoading && variant,
          showArrowSection && styles.selectGap,
          className,
        ])}
        onClick={isEmptyChildren ? onClick : () => setIsOpen(!isOpen)}
        ref={rootRef}
      >
        {isLoading && (
          <OrderCardSkeleton
            showContent={showContent}
            showArrowSection={showArrowSection}
            arrowContent={arrowContent}
            skeletonSize={skeletonSize}
          />
        )}
        {!isLoading && (
          <>
            {icon &&
              (typeof icon === "string" ? (
                <div
                  className={cn([
                    styles.iconWr,
                    animateIcon && styles.animated,
                  ])}
                >
                  <Icon type={icon as IconTypes} size={24} />
                </div>
              ) : (
                icon
              ))}
            {showContent && (
              <div className={styles.content}>
                <div
                  className={cn(
                    styles.time,
                    company.match({
                      mcosmetic: isHomePage
                        ? "text-navigation-text-in-button group-data-[scrolled=true]:text-navigation-text-primary"
                        : "text-navigation-text-secondary",
                      default: "text-navigation-text-secondary",
                    }),
                  )}
                  ref={(node) => {
                    if (!node) return;
                    const { width } = node.getBoundingClientRect();
                    skeletonSize.current.caption = width;
                  }}
                >
                  {caption}
                </div>
                {title && (
                  <div
                    className={cn(
                      styles.statusWr,
                      "transition-colors duration-300",
                      company.match({
                        mcosmetic: isHomePage
                          ? "text-navigation-text-in-button group-data-[scrolled=true]:text-navigation-text-primary"
                          : "text-navigation-text-primary",
                        default: "text-navigation-text-primary",
                      }),
                    )}
                    ref={(node) => {
                      if (!node) return;
                      const { width } = node.getBoundingClientRect();
                      skeletonSize.current.title = width;
                    }}
                  >
                    {title}
                  </div>
                )}
              </div>
            )}
            {showArrowSection && arrowContent}
          </>
        )}
      </div>
      {renderDropdown()}
    </>
  );
};

export default observer(MobileHeaderCard);
